﻿/*
 * Purpose: A lightweight, simple toast notification system inspired by toastr using React Context API.
 * Boundary: Manages toast state, rendering, and lifecycle via context; no external dependencies.
 * Invariants:
 * - Supports success, error, info, and warning types with auto-dismiss.
 * - Relies on existing Tailwind CSS in master page/layout.
 * - Toasts stack from bottom to top, mimicking toastr's default behavior.
 * - ASCII only; no Unicode in comments or strings.
 */

import { createContext, useContext, useState, useEffect, useCallback } from 'react';

// Create Toast Context
const ToastContext = createContext();

// Toast Provider Component
const ToastProvider = ({ children, position = 'bottom-right' }) => {
  const [toasts, setToasts] = useState([]);

  // Add a toast
  const addToast = useCallback((message, type = 'info', duration = 3000) => {
    const id = Date.now() + Math.random(); // Unique ID
    setToasts((prev) => [...prev, { id, message, type, duration }]);
  }, []);

  // Remove a toast
  const removeToast = useCallback((id) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  }, []);

  // Clear all toasts
  const clearToasts = useCallback(() => {
    setToasts([]);
  }, []);

  // Expose toastr methods via context
  const toastr = {
    success: (message, duration) => addToast(message, 'success', duration),
    error: (message, duration) => addToast(message, 'error', duration),
    info: (message, duration) => addToast(message, 'info', duration),
    warning: (message, duration) => addToast(message, 'warning', duration),
    clear: clearToasts,
  };

  // Shared position styles
  const positionStyles = {
    'top-right': 'top-4 right-4',
    'top-left': 'top-4 left-4',
    'bottom-right': 'bottom-4 right-4',
    'bottom-left': 'bottom-4 left-4',
  };

  // Single Toast Component
  const Toast = ({ id, message, type = 'info', duration = 2500, onClose }) => {
    useEffect(() => {
      if (!duration) return;
      const timer = setTimeout(() => onClose(id), duration);
      return () => clearTimeout(timer);
    }, [id, duration, onClose]);

    const typeStyles = {
      success: 'bg-green-600 text-white',
      error: 'bg-red-600 text-white',
      info: 'bg-blue-600 text-white',
      warning: 'bg-yellow-600 text-white',
    };

    return (
      <div
        className={`max-w-xs w-full p-2 rounded-md shadow-lg ${typeStyles[type]} transition-all duration-300`}
        role="alert"
      >
        <div className="flex items-center justify-between">
          <span className="text-sm">{message}</span>
          <button
            className="ml-1 text-white hover:text-gray-200 focus:outline-none text-sm"
            onClick={() => onClose(id)}
            aria-label="Close" 
            type="button"
          >
            &times;
          </button>
        </div>
      </div>
    );
  };

  // Determine flex direction and alignment based on position
  const isBottom = position.includes('bottom');
  const flexDirection = isBottom ? 'flex-col-reverse' : 'flex-col';
  const isRight = position.includes('right');
  const alignItems = isRight ? 'items-end' : 'items-start';

  return (
    <ToastContext.Provider value={toastr}>
      {children}
      <div
        className={`fixed ${positionStyles[position] || 'bottom-4 right-4'} z-50 flex ${flexDirection} ${alignItems} gap-y-1`}
      >
        {toasts.map((toast) => (
          <Toast
            key={toast.id}
            id={toast.id}
            message={toast.message}
            type={toast.type}
            duration={toast.duration}
            onClose={removeToast}
          />
        ))}
      </div>
    </ToastContext.Provider>
  );
};

// Custom Hook to Use Toast Context
const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};

export { ToastProvider, useToast };

/*import { ToastProvider, useToast } from './components/Toast/Toast';
function App() {
  return (
    <ToastProvider position="bottom-right">
      <YourApp />
    </ToastProvider>
  );
}

function YourComponent() {
  const toastr = useToast();
  const handleClick = () => toastr.success('Action completed!', 3000);
  return <button onClick={handleClick}>Show Toast</button>;
}
*/